import { useSheet } from "@/elements/components/shared/sheet-provider";
import { AppBar } from "@/elements/components/ui/app-bar";
import { Button } from "@/elements/components/ui/button";
import { CheckboxInput } from "@/elements/components/ui/checkbox-input";
import { api } from "@/elements/components/utils/convex-api";
import { SheetIds } from "@/elements/components/utils/sheet-ids";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useRouter } from "expo-router";
import React, { useCallback, useState } from "react";
import { Text, View } from "react-native";
import { useAuthActions } from "@convex-dev/auth/react";
import ResponsiveContent from "@/elements/components/shared/responsive-content";

function TutorLegalScreen() {
  const { signOut } = useAuthActions();
  const { data: currentUser } = useQuery(
    convexQuery(api.account.queries.getCurrentUser, {})
  );
  const [
    isConsentToRecordSessionsChecked,
    setIsConsentToRecordSessionsChecked,
  ] = useState(false);
  const [isTermsAndConditionsChecked, setIsTermsAndConditionsChecked] =
    useState(false);
  const [isDataProtectionPolicyChecked, setIsDataProtectionPolicyChecked] =
    useState(false);
  const { open } = useSheet<any, any>(SheetIds.LEGAL);
  const { data: termsOfService } = useQuery(
    {
      ...convexQuery(api.tutor.onboarding.queries.getLegalDocument, {
        documentType: "terms-and-conditions",
      }),
      enabled: !!currentUser,
    }
  );
  const { data: consentToRecordSessions } = useQuery(
    {
      ...convexQuery(api.tutor.onboarding.queries.getLegalDocument, {
        documentType: "consent-to-record-sessions",
      }),
      enabled: !!currentUser,
    }
  );
  const { data: dataProtectionPolicy } = useQuery(
    {
      ...convexQuery(api.tutor.onboarding.queries.getLegalDocument, {
        documentType: "data-protection",
      }),
      enabled: !!currentUser,
    }
  );
  const { mutateAsync: updateProfile } = useMutation({
    mutationFn: useConvexMutation(api.tutor.onboarding.mutations.updateProfile),
  });
  const router = useRouter();

  const handleSubmit = useCallback(async () => {
    await updateProfile({
      consentToRecordSessionsAcceptedAt: Date.now(),
      termsAndConditionsAcceptedAt: Date.now(),
      dataProtectionPolicyAcceptedAt: Date.now(),
      onboardingStepCompleted: "legal",
      onboardingCompletedAt: Date.now(),
    });
    router.replace("/(app)/home");
  }, [updateProfile, router]);

  const onConsentToRecordSessionsPress = useCallback(async () => {
    const payload = {
      title: "Consent to record sessions",
      content: consentToRecordSessions,
      status: isConsentToRecordSessionsChecked ? "accepted" : "pending",
    };
    const result = await open(payload);
    if (result?.status) {
      setIsConsentToRecordSessionsChecked(result.status === "accepted");
    }
  }, [open, isConsentToRecordSessionsChecked, consentToRecordSessions]);

  const onTermsAndConditionsPress = useCallback(async () => {
    const payload = {
      title: "Terms and conditions",
      content: termsOfService,
      status: isTermsAndConditionsChecked ? "accepted" : "pending",
    };
    const result = await open(payload);
    if (result?.status) {
      setIsTermsAndConditionsChecked(result.status === "accepted");
    }
  }, [open, isTermsAndConditionsChecked, termsOfService]);

  const onDataProtectionPolicyPress = useCallback(async () => {
    const payload = {
      title: "Data protection policy",
      content: dataProtectionPolicy,
      status: isDataProtectionPolicyChecked ? "accepted" : "pending",
    };
    const result = await open(payload);
    if (result?.status) {
      setIsDataProtectionPolicyChecked(result.status === "accepted");
    }
  }, [open, isDataProtectionPolicyChecked, dataProtectionPolicy]);

  return (
    <View className="flex-1 bg-teachLoop-creamBase">
      <AppBar>
        <AppBar.Icon onPress={() => signOut()} />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>
      <ResponsiveContent>
        <View className="mx-5">
          <View className="h-4" />
          <View className="gap-1">
            <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[28px] text-black">
              Almost there..
            </Text>
            <Text className="font-geologica-regular font-normal text-xl text-black leading-[1.5]">
              We need your consent to proceed.
            </Text>
          </View>
          <View className="h-8" />
          <View className="gap-4">
            <Text className="text-base leading-[1.5] font-geologica-medium font-medium text-black">
              I have read and agree to the following.
            </Text>
            <View className="gap-2">
              <CheckboxInput onPress={onConsentToRecordSessionsPress}>
                <CheckboxInput.Label size="lg">
                  Consent to record sessions
                </CheckboxInput.Label>
                <CheckboxInput.Icon
                  checked={isConsentToRecordSessionsChecked}
                />
              </CheckboxInput>

              <CheckboxInput onPress={onTermsAndConditionsPress}>
                <CheckboxInput.Label size="lg">
                  Tutor terms and conditions
                </CheckboxInput.Label>
                <CheckboxInput.Icon checked={isTermsAndConditionsChecked} />
              </CheckboxInput>

              <CheckboxInput onPress={onDataProtectionPolicyPress}>
                <CheckboxInput.Label size="lg">
                  Data protection policy
                </CheckboxInput.Label>
                <CheckboxInput.Icon checked={isDataProtectionPolicyChecked} />
              </CheckboxInput>
            </View>
          </View>
          <View className="h-8" />
          <Button
            className="w-full"
            intent="primary"
            disabled={
              !isConsentToRecordSessionsChecked ||
              !isTermsAndConditionsChecked ||
              !isDataProtectionPolicyChecked
            }
            onPress={handleSubmit}
          >
            <Button.Text>Submit application</Button.Text>
          </Button>
        </View>
      </ResponsiveContent>
    </View>
  );
}

export default TutorLegalScreen;
